import React, { useState, useEffect } from "react";
import styles from "./BaseSettings.module.css";
import withStyles from "@mui/styles/withStyles";
import { useSelector, useDispatch } from "react-redux";
import { settlementActions } from "@_actions/settlement_portal.actions";
import { fileActions } from "@_actions/files_settlement_portal.actions";
import { catchError, getUserDataFromJwt } from "@_utils/helpers";
import UpgradeDomainImg from "@_assets/images/your_domain.png";
import UpgradeLogoImg from "@_assets/images/your_logo.png";
import { sidebarActions } from "@_actions/sidebar.actions";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import DeleteIcon from "@mui/icons-material/Delete";
import ImportantDevicesIcon from "@mui/icons-material/ImportantDevices";
import TextfieldInput from "@_components/TextfieldInput/TextfieldInput";
import Button from "@mui/material/Button";
import UploadImage from "@_components/UploadImage/UploadImage";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import { sidebarConstants } from "@_constants/sidebar.constants";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

const CustomTabs = withStyles({
  root: {
    fontSize: 24,
    height: 60,
    backgroundColor: "var(--color-theme-white)",
  },
  indicator: {
    backgroundColor: "var(--color-theme-blue)",
  },
})(Tabs);

const CustomTab = withStyles((theme) => ({
  root: {
    height: 60,
    fontSize: 18,
    color: "var(--color-theme-10)",
    "&:hover": {
      backgroundColor: "var(--color-theme-lightblue)",
      opacity: 1,
    },
    "&$selected": {
      color: "var(--color-theme-blue)",
    },
    "&:focus": {
      color: "var(--color-theme-blue)",
    },
  },
  selected: {},
}))(Tab);

function BaseSettings() {
  const jwtData = getUserDataFromJwt();
  const subscription = jwtData.subscription;

  const dispatch = useDispatch();
  const settlement = useSelector((state) => state.settlementReducer);
  const settlementFiles = useSelector((state) => state.fileReducer);

  const [selectedTab, setSelectedTab] = useState(0);
  const [fileType, setFileType] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState({
    open: false,
    type: "",
  });

  const [settlementDetails, setSettlementDetails] = useState({
    name: "",
    homepage: "",
    settlement_portal_host_internal: "",
    settlement_portal_host_external: "",
    imprint_link: "",
    privacy_statement_link: "",
    about_us_text: "",
    login_text: "",
    case_overview_text: "",
    logo_url: "",
    favicon_url: "",
    about_us_image_url: "",
    login_page_image_url: "",
  });

  const [selectedFile, setSelectedFile] = useState({
    file: null,
    image_data: "",
    image_name: "",
  });

  const { snackbarProps, showSnackbarSuccess, showSnackbarError } =
    useSnackbar();
  const location = useLocation();
  const history = useHistory();
  const pathParts = location.pathname.split("/").filter(Boolean);

  // Load settlement on mount
  useEffect(() => {
    dispatch(settlementActions.getSettlement());
    dispatch(sidebarActions.updateStatus(sidebarConstants.BASE_SETTINGS));
    if (pathParts.length === 4 && pathParts[2] === "base_settings") {
      switch (pathParts[3]) {
        case "portal_address":
          setSelectedTab(0);
          break;
        case "legal_and_contact":
          setSelectedTab(1);
          break;
        case "logo_and_images":
          setSelectedTab(2);
          break;
        default:
          history.push("/page_not_found");
          break;
      }
    }
    // eslint-disable-next-line
  }, [dispatch]);

  // Update local state when settlement data changes
  useEffect(() => {
    setSettlementDetails({
      name: settlement.settlement_details.name || "",
      homepage: settlement.settlement_details.homepage || "",
      settlement_portal_host_internal:
        settlement.settlement_details.settlement_portal_host_internal || "",
      settlement_portal_host_external:
        settlement.settlement_details.settlement_portal_host_external || "",
      imprint_link: settlement.settlement_details.imprint_link || "",
      privacy_statement_link:
        settlement.settlement_details.privacy_statement_link || "",
      about_us_text: settlement.settlement_details.about_us_text || "",
      login_text: settlement.settlement_details.login_text || "",
      case_overview_text:
        settlement.settlement_details.case_overview_text || "",
      logo_url: settlement.settlement_details.logo_url || "",
      favicon_url: settlement.settlement_details.favicon_url || "",
      about_us_image_url:
        settlement.settlement_details.about_us_image_url || "",
      login_page_image_url:
        settlement.settlement_details.login_page_image_url || "",
    });
  }, [settlement.settlement_details]);

  // Convert file to base64
  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Handle file input change
  const handleFileInputChange = async (e) => {
    const file = e.target.files?.[0];

    // Clear input
    e.target.value = "";

    if (!file) {
      return;
    }

    try {
      // Convert to base64
      const base64Result = await getBase64(file);

      // Update state once with all data
      setSelectedFile({
        file,
        image_data: base64Result,
        image_name: file.name,
      });
    } catch (error) {
      console.error("Error converting file to base64:", error);
      showSnackbarError("Failed to process image");
    }
  };

  // Upload file when selectedFile and fileType are set
  useEffect(() => {
    if (!selectedFile.image_data || !selectedFile.image_name || !fileType) {
      return;
    }

    const uploadFile = () => {
      try {
        const { image_data, image_name } = selectedFile;

        // Remove base64 prefix
        const base64Data = image_data.split(";base64,")[1];

        if (!base64Data) {
          showSnackbarError("Invalid image data");
          return;
        }

        const data = {
          image_data: base64Data,
          image_name,
        };

        // Dispatch upload action
        dispatch(fileActions.updateFiles(fileType, data));

        // Clear file selection
        setSelectedFile({
          file: null,
          image_data: "",
          image_name: "",
        });

        // Clear file type after upload is dispatched
        setFileType("");
      } catch (error) {
        console.error("Upload error:", error);
        showSnackbarError("Failed to upload file");
      }
    };

    uploadFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile.image_data, selectedFile.image_name, fileType]);

  // Refresh settlement after successful file upload
  useEffect(() => {
    if (settlementFiles.status.FILE_PUT === "success") {
      dispatch(settlementActions.getSettlement());
    }
  }, [dispatch, settlementFiles.status.FILE_PUT]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettlementDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitPortalAddress = () => {
    const { settlement_portal_host_internal, settlement_portal_host_external } =
      settlementDetails;
    const portalAddressData = {
      settlement_portal_host_internal,
      settlement_portal_host_external,
    };
    dispatch(settlementActions.updatePortalAddress(portalAddressData));
  };

  const handleSubmitLegalContact = () => {
    const { email, privacy_statement_link, imprint_link, homepage } =
      settlementDetails;
    const legalContactData = { email };

    if (homepage && homepage.length > 0) {
      legalContactData["homepage"] = homepage;
    }

    if (privacy_statement_link && privacy_statement_link.length > 0) {
      legalContactData["privacy_statement_link"] = privacy_statement_link;
    }

    if (imprint_link && imprint_link.length > 0) {
      legalContactData["imprint_link"] = imprint_link;
    }

    dispatch(settlementActions.updateLegalContact(legalContactData));
  };

  const openDeleteDialog = (type) => {
    setDeleteDialogOpen({ open: true, type });
  };

  const handleDeleteFile = (type) => {
    const urlMap = {
      logo: settlement.settlement_details.logo_url,
      favicon: settlement.settlement_details.favicon_url,
      about_us_image: settlement.settlement_details.about_us_image_url,
      login_page_image: settlement.settlement_details.login_page_image_url,
    };

    const imageUrl = urlMap[type];

    if (!imageUrl) {
      setDeleteDialogOpen({ open: false, type: "" });
      return;
    }

    const index = imageUrl.lastIndexOf("/") + 1;
    const image_name = imageUrl.substr(index);

    dispatch(fileActions.deleteFiles(type, image_name));
    setDeleteDialogOpen({ open: false, type: "" });
  };

  const handleUpgrade = () => {
    dispatch(sidebarActions.updateStatus("SUBSCRIPTION"));
  };

  const handleTabChange = (event, newValue) => {
    history.push(
      `/admin/settlement_portal/base_settings/${
        newValue === 0
          ? "portal_address"
          : newValue === 1
            ? "legal_and_contact"
            : "logo_and_images"
      }`,
    );
    setSelectedTab(newValue);
  };

  // Handle settlement PUT success/error messages
  useEffect(() => {
    if (settlement.status.SETTLEMENT_PUT === "success") {
      showSnackbarSuccess("Success!");
      setTimeout(() => {
        dispatch(settlementActions.clearStatusMessage("SETTLEMENT_PUT"));
      }, 4000);
    } else if (catchError(settlement.status.SETTLEMENT_PUT)) {
      showSnackbarError(settlement.status.SETTLEMENT_PUT, {
        autoHideDuration: 7000,
      });
      setTimeout(() => {
        dispatch(settlementActions.clearStatusMessage("SETTLEMENT_PUT"));
      }, 7000);
    }
  }, [
    dispatch,
    settlement.status.SETTLEMENT_PUT,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  // Handle file upload PUT success/error messages
  useEffect(() => {
    if (settlementFiles.status.FILE_PUT === "success") {
      showSnackbarSuccess("Success!");
      setTimeout(() => {
        dispatch(fileActions.clearStatusMessage("FILE_PUT"));
      }, 4000);
    } else if (catchError(settlementFiles.status.FILE_PUT)) {
      let errorMsg = settlementFiles.status.FILE_PUT;
      showSnackbarError(errorMsg, { autoHideDuration: 7000 });
      setTimeout(() => {
        dispatch(fileActions.clearStatusMessage("FILE_PUT"));
      }, 7000);
    }
  }, [
    dispatch,
    settlementFiles.status.FILE_PUT,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  // Handle file DELETE success/error messages
  useEffect(() => {
    if (settlementFiles.status.FILE_DELETE === "success") {
      showSnackbarSuccess("Success!");
      dispatch(settlementActions.getSettlement());
      setTimeout(() => {
        dispatch(fileActions.clearStatusMessage("FILE_DELETE"));
      }, 4000);
    } else if (catchError(settlementFiles.status.FILE_DELETE)) {
      let errorMsg = settlementFiles.status.FILE_DELETE;
      showSnackbarError(errorMsg, { autoHideDuration: 7000 });
      setTimeout(() => {
        dispatch(fileActions.clearStatusMessage("FILE_DELETE"));
      }, 7000);
    }
  }, [
    dispatch,
    settlementFiles.status.FILE_DELETE,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  return (
    <div className={styles.root}>
      <AppSnackbar {...snackbarProps} />

      <div className={styles.settlement_portal_title}>
        <ImportantDevicesIcon className={styles.device_icon} />
        <h2>Settlement Portal Settings </h2>
        <ArrowForwardIosIcon fontSize="small" className={styles.arrow_icon} />
        <h2>Base Settings</h2>
      </div>
      <p>
        Base settings that apply universally to all translations of the
        settlement portal.
      </p>

      <div className={styles.tabs_root}>
        <div className={styles.tabs_container}>
          <CustomTabs
            value={selectedTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            variant="fullWidth"
          >
            <CustomTab label="Portal Address *" />
            <CustomTab label="Legal & Contact" />
            <CustomTab label="Logo & Images" />
          </CustomTabs>
        </div>

        <div
          className={
            selectedTab !== 2
              ? styles.tabs_content_container
              : styles.tabs_content_container_images
          }
        >
          {/* PORTAL ADDRESS TAB */}
          <div
            hidden={selectedTab !== 0}
            className={styles.tab_content_portal_address}
          >
            <div className={styles.subdomain_container}>
              <p className={styles.label}>Subdomain in fairlicensing.com</p>
              <p className={styles.description}>
                Set the subdomain that your settlement portal can be accessed on
                in the fairlicensing.com domain (i.e.
                subdomain.fairlicensing.com)
              </p>
              <div className={styles.example_input_container}>
                <div className={styles.input_container}>
                  <TextfieldInput
                    label="Subdomain in fairlicensing.com"
                    id="settlement_portal_host_internal"
                    name="settlement_portal_host_internal"
                    value={settlementDetails.settlement_portal_host_internal}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <p>.fairlicensing.com</p>
              </div>
              <div className={styles.border}></div>
              {subscription === "LITE" && (
                <div className={styles.confirm_btn_container}>
                  <Button
                    className={styles.confirm_btn}
                    onClick={handleSubmitPortalAddress}
                  >
                    Save
                  </Button>
                </div>
              )}
            </div>

            <div>
              {subscription === "LITE" ? (
                <div className={styles.upgrade_container}>
                  <div>
                    <img
                      src={UpgradeDomainImg}
                      alt="own domain illustration"
                      className={styles.upgrade_domain_image}
                    />
                  </div>
                  <div className={styles.upgrade_content}>
                    <p className={styles.upgrade_text_large}>
                      Gain trust! Intergrate the settlement portal in your own
                      domain.
                    </p>
                    <div className={styles.upgrade_btn_container}>
                      <Button
                        className={styles.confirm_btn}
                        onClick={handleUpgrade}
                      >
                        Upgrade To Pro
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className={styles.label}>
                    Settlement Portal in your own Domain
                  </p>
                  <p className={styles.description}>
                    Optionally choose to publish the settlement portal in your
                    own domain (i.e. fairlicensing.yourdomain.com).
                    <br />
                    <br />
                    In order for your settlement portal to be accessible in your
                    own domain you have to set a CNAME record in your domain
                    configuration pointing from fairlicensing.yourdomain.com to
                    settlement.fairlicensing.com.
                  </p>
                  <div className={styles.example_input_container}>
                    <p>fairlicensing.</p>
                    <div className={styles.input_container}>
                      <TextfieldInput
                        label="Your Domain"
                        id="settlement_portal_host_external"
                        name="settlement_portal_host_external"
                        value={
                          settlementDetails.settlement_portal_host_external
                        }
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            {subscription !== "LITE" && (
              <div className={styles.confirm_btn_container}>
                <Button
                  className={styles.confirm_btn}
                  onClick={handleSubmitPortalAddress}
                >
                  Save
                </Button>
              </div>
            )}
          </div>

          {/* LEGAL & CONTACT TAB */}
          <div
            hidden={selectedTab !== 1}
            className={styles.tab_content_legal_contact}
          >
            <p className={styles.description}>
              Set mandatory links and contact info that will be displayed in the
              settlement portal.
            </p>
            <div className={styles.legal_contact_container}>
              <div className={styles.input_row}>
                <div className={styles.input_container}>
                  <TextfieldInput
                    label="Company Homepage"
                    id="homepage"
                    name="homepage"
                    value={settlementDetails.homepage}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={styles.input_container}>
                  <TextfieldInput
                    label="Privacy Statement Link"
                    id="privacy_statement_link"
                    name="privacy_statement_link"
                    value={settlementDetails.privacy_statement_link}
                    onChange={handleInputChange}
                    className={styles.link_input}
                  />
                </div>
              </div>
              <div className={styles.input_row}>
                <div className={styles.input_container}>
                  <TextfieldInput
                    label="Contact Page Link"
                    id="imprint_link"
                    name="imprint_link"
                    value={settlementDetails.imprint_link}
                    onChange={handleInputChange}
                    className={styles.link_input}
                  />
                </div>
              </div>
            </div>
            <div className={styles.confirm_btn_container}>
              <Button
                className={styles.confirm_btn}
                onClick={handleSubmitLegalContact}
              >
                Save
              </Button>
            </div>
          </div>

          {/* LOGO & IMAGES TAB */}
          <div hidden={selectedTab !== 2} className={styles.tab_content}>
            <div className={styles.logo_favicon_container}>
              {subscription === "LITE" ? (
                <div className={styles.upgrade_container}>
                  <div>
                    <img
                      src={UpgradeLogoImg}
                      alt="upload logo illustration"
                      className={styles.upgrade_logo_image}
                    />
                  </div>
                  <div className={styles.upgrade_content}>
                    <p className={styles.upgrade_text_large}>
                      Customize your settlement portal by using your own logo
                      <br />
                      and your own image on the 'about us' page.
                    </p>
                    <div className={styles.upgrade_btn_container}>
                      <Button
                        className={styles.confirm_btn}
                        onClick={handleUpgrade}
                      >
                        Upgrade To Pro
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className={styles.description}>
                    Upload images and logos that will be displayed in the
                    settlement portal.
                  </p>
                  <div className={styles.general_col_images}>
                    {/* LOGO */}
                    <div className={styles.logo_container}>
                      <div className={styles.general_image_container}>
                        <p className={styles.image_label}>Logo</p>
                        <div
                          className={styles.logo_box}
                          onClick={() => setFileType("logo")}
                        >
                          {settlement.settlement_details.logo_url ? (
                            <div>
                              <div
                                className={styles.delete_icon_container_logo}
                              >
                                <DeleteIcon
                                  className={styles.delete_icon}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteDialog("logo");
                                  }}
                                  fontSize="small"
                                />
                              </div>
                              <img
                                src={settlement.settlement_details.logo_url}
                                alt="logo"
                                className={styles.logo}
                              />
                            </div>
                          ) : (
                            <UploadImage
                              styles={styles}
                              id="logo"
                              accept=".jpg, .gif, .png, .jpeg, .ico"
                              handleFileInputChange={handleFileInputChange}
                            />
                          )}
                        </div>
                      </div>
                      <p className={styles.upload_size}>
                        Supported dimensions:
                      </p>
                      <span className={styles.dimension_list}>
                        width: 35px - 1600px
                      </span>
                      <br />
                      <span className={styles.dimension_list}>
                        height: 35px - 400px
                      </span>
                    </div>

                    {/* FAVICON */}
                    <div className={styles.favicon_container}>
                      <div className={styles.general_image_container}>
                        <p className={styles.image_label}>Favicon</p>
                        <div
                          className={styles.favicon_box}
                          onClick={() => setFileType("favicon")}
                        >
                          {settlement.settlement_details.favicon_url ? (
                            <div>
                              <div
                                className={styles.delete_icon_container_favicon}
                              >
                                <DeleteIcon
                                  className={styles.delete_icon}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteDialog("favicon");
                                  }}
                                  fontSize="small"
                                />
                              </div>
                              <img
                                src={`${
                                  settlement.settlement_details.favicon_url
                                }?${new Date().getTime()}`}
                                className={styles.favicon}
                                alt="favicon"
                              />
                            </div>
                          ) : (
                            <UploadImage
                              styles={styles}
                              id="favicon"
                              handleFileInputChange={handleFileInputChange}
                              accept=".gif, .png, .ico"
                            />
                          )}
                        </div>
                        <p className={styles.upload_size}>
                          Supported dimensions:
                        </p>
                        <span className={styles.dimension_list}>
                          16/32/48/64/128px square size
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ABOUT US & LOGIN PAGE IMAGES */}
            {subscription !== "LITE" && (
              <div className={styles.portal_image_section}>
                <div className={styles.upload_image_container}>
                  {/* LOGIN PAGE IMAGE (moved first) */}
                  <div className={styles.portal_image_preview_container}>
                    <p className={styles.image_label}>Login</p>
                    <div
                      className={styles.portal_image_box}
                      onClick={() => setFileType("login_page_image")}
                    >
                      {settlement.settlement_details.login_page_image_url ? (
                        <div>
                          <div className={styles.portal_image_delete}>
                            <DeleteIcon
                              className={styles.delete_icon}
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteDialog("login_page_image");
                              }}
                              fontSize="small"
                            />
                          </div>
                          <img
                            src={
                              settlement.settlement_details.login_page_image_url
                            }
                            alt="login page"
                            className={styles.portal_image_preview}
                          />
                        </div>
                      ) : (
                        <UploadImage
                          styles={styles}
                          id="login_page_image"
                          accept=".jpg, .jpeg, .png"
                          handleFileInputChange={handleFileInputChange}
                        />
                      )}
                    </div>
                    <p className={styles.upload_size}>Supported dimensions: </p>
                    <span className={styles.dimension_list}>
                      width/height: 950px - 2400px
                    </span>
                    <br />
                  </div>

                  {/* ABOUT US IMAGE (moved second) */}
                  <div className={styles.portal_image_preview_container}>
                    <p className={styles.image_label}>About us</p>
                    <div
                      className={styles.portal_image_box}
                      onClick={() => setFileType("about_us_image")}
                    >
                      {settlement.settlement_details.about_us_image_url ? (
                        <div>
                          <div className={styles.portal_image_delete}>
                            <DeleteIcon
                              className={styles.delete_icon}
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteDialog("about_us_image");
                              }}
                              fontSize="small"
                            />
                          </div>
                          <img
                            src={
                              settlement.settlement_details.about_us_image_url
                            }
                            alt="about us"
                            className={styles.portal_image_preview}
                          />
                        </div>
                      ) : (
                        <UploadImage
                          styles={styles}
                          id="about_us_image"
                          accept=".jpg, .jpeg, .png"
                          handleFileInputChange={handleFileInputChange}
                        />
                      )}
                    </div>
                    <p className={styles.upload_size}>Supported dimensions: </p>
                    <span className={styles.dimension_list}>
                      width: 100–2000 px
                    </span>
                    <br />
                    <span className={styles.dimension_list}>
                      height: 120–2400 px
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* DELETE CONFIRMATION DIALOG */}
      {deleteDialogOpen.open && (
        <DialogPopup
          title="Confirmation required"
          open={deleteDialogOpen.open}
          onClose={() => setDeleteDialogOpen({ open: false, type: "" })}
        >
          <div className={styles.deletionPopupText}>
            Do you really want to delete the{" "}
            {deleteDialogOpen.type === "about_us_image"
              ? "About Us image"
              : deleteDialogOpen.type === "login_page_image"
                ? "Login page image"
                : deleteDialogOpen.type}
            ?
          </div>

          <div className={styles.deletionPopupButtonContainer}>
            <Button
              className={styles.button_confirm}
              onClick={() => handleDeleteFile(deleteDialogOpen.type)}
            >
              Yes
            </Button>
            <Button
              className={styles.button_cancel}
              onClick={() => setDeleteDialogOpen({ open: false, type: "" })}
            >
              Cancel
            </Button>
          </div>
        </DialogPopup>
      )}
    </div>
  );
}

export default BaseSettings;
